import { faBan, faCheckCircle, faExclamationTriangle, faInfoCircle, faTimesCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface PreparationStatusMessageProps {
    status: number;
    type: number;
    preparationCode: string;
    reason: 'status' | 'type';
}

export const PreparationStatusMessage: React.FC<PreparationStatusMessageProps> = ({
    status,
    type,
    preparationCode,
    reason
}) => {
    const { t } = useTranslation('pickingModule');

    const getStatusConfig = (reason: 'status' | 'type', status: number, type: number) => {
        if (reason === 'type') {
            return {
                icon: faBan,
                title: t('status.wrong_type'),
                message: t('messages.wrong_type_explanation'),
                bgColor: 'bg-orange-50 dark:bg-orange-950/20',
                borderColor: 'border-orange-200 dark:border-orange-800',
                textColor: 'text-orange-700 dark:text-orange-300',
                iconColor: 'text-orange-500'
            };
        }

        switch (status) {
            case 0: // Cancelado
                return {
                    icon: faTimesCircle,
                    title: t('status.cancelled'),
                    message: t('messages.cannot_pick_cancelled'),
                    bgColor: 'bg-red-50 dark:bg-red-950/20',
                    borderColor: 'border-red-200 dark:border-red-800',
                    textColor: 'text-red-700 dark:text-red-300',
                    iconColor: 'text-red-500'
                };
            case 2: // Completado
            case 3: // Completado
                return {
                    icon: faCheckCircle,
                    title: t('status.completed'),
                    message: t('messages.cannot_pick_completed'),
                    bgColor: 'bg-green-50 dark:bg-green-950/20',
                    borderColor: 'border-green-200 dark:border-green-800',
                    textColor: 'text-green-700 dark:text-green-300',
                    iconColor: 'text-green-500'
                };
            default:
                return {
                    icon: faExclamationTriangle,
                    title: t('status.not_available'),
                    message: t('messages.cannot_pick_cancelled'),
                    bgColor: 'bg-yellow-50 dark:bg-yellow-950/20',
                    borderColor: 'border-yellow-200 dark:border-yellow-800',
                    textColor: 'text-yellow-700 dark:text-yellow-300',
                    iconColor: 'text-yellow-500'
                };
        }
    };

    const config = getStatusConfig(reason, status, type);

    return (
        <div className="container mx-auto p-4 max-w-4xl">
            <div className={`${config.bgColor} ${config.borderColor} border rounded-lg p-8 text-center`}>
                {/* Icono principal */}
                <div className="mb-6">
                    <FontAwesomeIcon
                        icon={config.icon}
                        className={`w-16 h-16 mx-auto ${config.iconColor}`}
                    />
                </div>

                {/* Título */}
                <h2 className={`text-2xl font-bold mb-4 ${config.textColor}`}>
                    {config.title}
                </h2>

                {/* Código de preparación */}
                <div className="mb-4">
                    <span className="text-lg font-medium text-muted-foreground">
                        Preparación:
                    </span>
                    <span className="text-lg font-bold text-foreground ml-2">
                        {preparationCode}
                    </span>
                </div>

                {/* Mensaje explicativo */}
                <p className={`text-lg mb-6 ${config.textColor}`}>
                    {config.message}
                </p>

                {/* Información adicional */}
                <div className={`${config.bgColor} border ${config.borderColor} rounded-lg p-4 inline-block`}>
                    <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faInfoCircle} className={`w-4 h-4 ${config.iconColor}`} />
                        <span className={`text-sm ${config.textColor}`}>
                            {t('messages.contact_supervisor')}
                        </span>
                    </div>
                </div>

                {/* Estado detallado */}
                <div className="mt-6 pt-4 border-t border-border/50">
                    <div className="text-sm text-muted-foreground space-y-1">
                        <div>
                            <span>Estado actual: </span>
                            <span className="font-medium">
                                {status === 0 && 'Cancelado (0)'}
                                {status === 1 && 'Disponible para picking (1)'}
                                {status === 2 && 'Completado (2)'}
                                {status === 3 && 'Completado (3)'}
                                {![0, 1, 2, 3].includes(status) && `Estado ${status}`}
                            </span>
                        </div>
                        <div>
                            <span>{t('messages.current_type')}: </span>
                            <span className={`font-medium ${reason === 'type' ? 'text-orange-600 dark:text-orange-400' : ''}`}>
                                {type} {reason === 'type' && '(se requiere tipo 3)'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}; 